import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Real OFLIC session state (M2), replacing the M1 placeholder gate.
 *
 * The browser holds only the HttpOnly refresh cookie (set server-side by the
 * OAuth callback). This hook calls GET /auth/me, which refreshes tokens
 * server-side against that cookie and returns a fresh short-lived access token
 * plus identity. The access token lives in memory only — never in
 * localStorage, never in a cookie, never in a URL.
 */
export interface Session {
  token: string;
  email: string;
  instance?: string;
  instances?: string[];
  organisation_id?: string;
  organisation_name?: string;
  expires_in: number;
}

export interface SessionState {
  session: Session | null;
  loading: boolean;
  beginLogin: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<Session | null>;
}

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const inflight = useRef<Promise<Session | null> | null>(null);

  const refresh = useCallback((): Promise<Session | null> => {
    // De-dupe concurrent refreshes (e.g. a 401 retry racing the boot check).
    if (inflight.current) return inflight.current;
    const p = (async (): Promise<Session | null> => {
      try {
        const res = await fetch("/auth/me", { credentials: "include" });
        if (!res.ok) {
          setSession(null);
          return null;
        }
        const s = (await res.json()) as Session;
        setSession(s);
        return s;
      } catch {
        setSession(null);
        return null;
      } finally {
        inflight.current = null;
      }
    })();
    inflight.current = p;
    return p;
  }, []);

  useEffect(() => {
    let active = true;
    refresh().finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [refresh]);

  const beginLogin = useCallback(() => {
    // Full-page redirect to the backend, which redirects on to OFLIC's hosted
    // login. return_to is a same-origin relative path (sanitised server-side).
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/oauth/authorize?return_to=${returnTo}`;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await fetch("/auth/logout", { method: "POST", credentials: "include" });
    } finally {
      setSession(null);
    }
  }, []);

  return { session, loading, beginLogin, logout, refresh };
}
